import type { loader as rootLoader } from "../../root";
import {
  json,
  redirect,
} from "@remix-run/cloudflare";
import type {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  MetaFunction,
} from "@remix-run/cloudflare";
import {
  Link,
  useLoaderData,
  useRouteLoaderData,
  useSearchParams,
} from "@remix-run/react";
import clsx from "clsx";
import {
  useEffect,
  useMemo,
  useState,
} from "react";
import { PostService } from "~/database/post.service";
import { UserService } from "~/database/user.service";
import { createSbClientSSR } from "~/utils/createSbClientSSR";
import { generateFileURL } from "~/utils/file/generateFileURL";
import { buttonBase } from "~/components/Common/FormStyle";
import UserSnsList from "~/components/User/UserSnsList";
import {
  DEFAULT_DESCRIPTION,
  TITLE_SUFFIX,
} from "~/utils/const";
import { FollowService } from "~/database/follow.service";
import { UserPosts } from "./UserPosts";
import { UserStoryPosts } from "./UserStoryPosts";
import { FollowUsers } from "./FollowListModal";
import { convertName } from "~/utils/convertName";
import { CompanyMembersService } from "~/database/company_members.service";
import { CompanyGalleriesService } from "~/database/company_galleries.service";
import { CompanyMessagesService } from "~/database/company_messages.service";
import CompanyMembersList from "~/components/User/CompanyMembersList";
import CompanyGalleriesList from "~/components/User/CompanyGalleriesList";
import CompanyMessage from "~/components/User/CompanyMessage";
import CompanyInfo from "~/components/User/CompanyInfo";
//import FollowBtn from "~/components/Icon/FollowButton";
import UserCount from "~/components/User/UserCount";
import toast from "react-hot-toast";
import { UserService2 } from "~/database/user2.service";

export const meta: MetaFunction<
  typeof loader
> = ({ data }) => {
  return [
    { title: data?.user.name + TITLE_SUFFIX },
    {
      name: "description",
      content: DEFAULT_DESCRIPTION,
    },
  ];
};

export async function loader({
  request,
  context,
  params,
}: LoaderFunctionArgs) {
  try {
    const displayId = params.userId as string;
    const headers = new Headers();
    const supabase = createSbClientSSR(
      headers,
      request,
      context,
    );

    const __user =
      await UserService2.getUserByDisplayId(
        supabase,
        displayId,
      );
    if (!__user.data) {
      const _user = await UserService2.getUser(
        supabase,
        displayId,
      );
      if (_user.data) {
        return redirect(
          `/user/${_user.data.display_id}`,
          {
            headers: headers,
          },
        );
      }
    }

    if (!__user.data) {
      return redirect("/");
    }
    const user = __user.data;

    const userId = user.id;
    const userPostsCursor =
      await PostService.getUserPostsCursor(
        supabase,
        userId,
        undefined,
        {
          limit: 4,
        },
      );
    const userStoryPostsCursor =
      await PostService.getUserStoryPostsCursor(
        supabase,
        userId,
        undefined,
        {
          limit: 6,
        },
      );
    const followCursor =
      await FollowService.getFollowUser(
        supabase,
        userId,
        undefined,
        {
          limit: 24,
        },
      );
    const followerCursor =
      await FollowService.getFollowerUser(
        supabase,
        userId,
        undefined,
        {
          limit: 24,
        },
      );
    const userProfile =
      await UserService.getUserProfile(
        supabase,
        userId,
      );

    // 会社メンバーとギャラリー、メッセージデータを取得
    const companyMembers =
      await CompanyMembersService.getPublicMembers(
        supabase,
        userId,
      );
    const companyGalleries =
      await CompanyGalleriesService.getPublicGalleries(
        supabase,
        userId,
      );
    const companyMessage =
      await CompanyMessagesService.getPublicMessage(
        supabase,
        userId,
      );

    let isFollow = false;
    const loginUserId = (
      await supabase.auth.getUser()
    )?.data?.user?.id;
    if (loginUserId) {
      isFollow = await FollowService.isFollow(
        supabase,
        userId,
      );
    }

    const authUser =
      await supabase.auth.getUser();
    if (authUser.data.user?.id === userId) {
      const likedPostsCursor =
        await PostService.getLikedPostsCursor(
          supabase,
          userId,
          undefined,
          {
            limit: 24,
          },
        );
      return json(
        {
          followCursor,
          followerCursor,
          userPostsCursor: userPostsCursor,
          userStoryPostsCursor:
            userStoryPostsCursor,
          likedPostsCursor: likedPostsCursor,
          user: user,
          isFollow,
          userProfile: userProfile,
          companyMembers: companyMembers,
          companyGalleries: companyGalleries,
          companyMessage: companyMessage,
        },
        {
          headers: headers,
        },
      );
    } else {
      return json(
        {
          followCursor,
          followerCursor,
          userPostsCursor: userPostsCursor,
          userStoryPostsCursor:
            userStoryPostsCursor,
          likedPostsCursor: null,
          user: user,
          isFollow,
          userProfile: userProfile,
          companyMembers: companyMembers,
          companyGalleries: companyGalleries,
          companyMessage: companyMessage,
        },
        {
          headers: headers,
        },
      );
    }
  } catch (e) {
    return redirect("/");
  }
}

export async function action({
  request,
  context,
}: ActionFunctionArgs) {
  const headers = new Headers();
  const supabase = createSbClientSSR(
    headers,
    request,
    context,
  );
  const userResponse =
    await supabase.auth.getUser();
  if (!userResponse?.data?.user?.id) {
    return redirect("/login");
  }
  const newPost = await PostService.createPosts(
    supabase,
    {
      title: "test",
      content_path: "",
      is_story: false,
    },
    userResponse?.data?.user?.id,
  );
  if (newPost.error) {
    return json(
      { error: newPost.error },
      { status: 500, headers },
    );
  }
  return redirect(
    `/posts/edit/${newPost.data?.id}`,
    {
      headers,
    },
  );
}

export default function Index() {
  const {
    user,

    userProfile,
    companyMembers,
    companyGalleries,
    companyMessage,
  } = useLoaderData<typeof loader>();

  const root =
    useRouteLoaderData<typeof rootLoader>("root");

  /* const { user: authUser } = useAuthUser(); */
  const isMyPage = useMemo(() => {
    return root?.user?.id === user.id;
  }, [root?.user?.id, user.id]);
  const [followModalOpen, setFollowModalOpen] =
    useState(false);
  const [
    followerModalOpen,
    setFollowerModalOpen,
  ] = useState(false);

  const [selectType] = useState<"user" | "like">(
    "user",
  );
  const {
    userPostsCursor,
    userStoryPostsCursor,
  } = useLoaderData<typeof loader>();

  const params = useSearchParams()[0];
  const isEdited = params.get("profileEdited");

  useEffect(() => {
    if (isEdited) {
      toast.success("保存しました", {
        duration: 4000,
      });
    }
  }, [isEdited]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    const complete =
      url.searchParams.get("complete");
    if (complete === "1") {
      toast.success("有料会員になりました！", {
        duration: 4000,
      });
    }
  }, []);

  return (
    <div
      className={clsx(
        "pb-128 pt-0",
        "max-md:pt-60",
        "max-md:pb-30",
      )}
    >
      <div className="">
        <div className="">
          <div
            className={clsx(
              "aspect-[4/1]",
              "w-full bg-gray-200",
              "max-md:aspect-[390/130]",
            )}
          >
            {user.coverurl && (
              <img
                className={clsx(
                  "h-full w-full object-cover",
                )}
                src={generateFileURL(
                  user.coverurl,
                  undefined,
                  root?.basePath,
                )}
                alt=""
              />
            )}
          </div>

          <div
            className={clsx(
              "mx-auto -mt-40 grid grid-cols-[auto_1fr] gap-32",
              "w-1300",
              "mx-auto",
              "max-md:w-360",
              "max-md:block",
            )}
          >
            <div
              className={clsx(
                "aspect-square",
                "w-196 rounded-full bg-gray-200",
                "max-md:w-71",
              )}
            >
              {user.iconurl && (
                <img
                  className={clsx(
                    "border-rad h-full w-full rounded-full border border-black object-cover",
                  )}
                  src={generateFileURL(
                    user.iconurl,
                    undefined,
                    root?.basePath,
                  )}
                  alt=""
                />
              )}
            </div>
            <div
              className={clsx(
                "grid grid-cols-[1fr_auto] gap-32",
                "pt-64",
                "max-md:block",
                "max-md:pt-20",
              )}
            >
              <div
                className={clsx(
                  "flex",
                  "gap-20",
                  "flex-col",
                )}
              >
                <div
                  className={clsx(
                    "text-20 font-bold",
                    "flex",
                    "gap-x-30",
                    "gap-y-10",
                    "grid",
                    "grid-cols-[auto_1fr]",
                    "items-center",
                    "max-md:gap-x-10",
                    "max-md:gap-y-10",
                    "grid-rows-[auto_auto]",
                  )}
                >
                  <div>
                    <div className={clsx()}>
                      {convertName(user.name)}
                    </div>
                    <div
                      className={clsx(
                        "text-14",
                        "text-gray-500",
                        "mt-4",
                      )}
                    >
                      @{user.display_id}
                    </div>
                  </div>
                  <UserCount
                    user={user}
                    setFollowerModalOpen={
                      setFollowerModalOpen
                    }
                    setFollowModalOpen={
                      setFollowModalOpen
                    }
                  ></UserCount>
                  <div
                    className={clsx(
                      "col-[2/3]",
                      "row-[1/2]",
                    )}
                  >
                    <UserSnsList
                      user={user}
                    ></UserSnsList>
                  </div>
                </div>
                <div
                  className={clsx(
                    "grid",
                    "gap-30",
                  )}
                >
                  <div
                    className={clsx(
                      "text-16",
                      "whitespace-pre-wrap",
                      "max-md:text-14",
                    )}
                  >
                    {user.profile}
                  </div>
                </div>
              </div>
              <div className={clsx("w-350")}>
                {!isMyPage && (
                  <div
                    className={clsx(
                      "flex",
                      "justify-center",
                      "max-md:mt-10",
                      "w-250",
                      "ml-auto",
                      "max-md:mr-auto",
                    )}
                  >
                    {/*        <FollowBtn
                      userId={user.id}
                      isFollow={isFollow}
                    ></FollowBtn> */}
                  </div>
                )}
                {isMyPage && (
                  <div
                  /*  className={clsx("pc_only")} */
                  >
                    <div
                      className={clsx(
                        "flex",
                        "flex-col",
                        "gap-16",
                        "mt-0",
                        "w-250",
                        "ml-auto",
                        "max-md:mr-auto",
                        "max-md:mt-30",
                      )}
                    >
                      <Link
                        className={clsx(
                          buttonBase({
                            variant: "ol",
                            size: "md",
                          }),
                          "px-50",
                        )}
                        to={"/profile/edit/"}
                      >
                        プロフィール編集
                      </Link>
                    </div>

                    {/*          {root?.subscription_active && (
                      <div
                        className={clsx(
                          "text-center",
                          "mt-16",
                        )}
                      >
                        <Link
                          to={`/cancel`}
                          className={clsx(
                            "text-sm",
                            "text-12",
                            "text-gray-400",
                            "underline",
                          )}
                        >
                          退会はこちらから
                        </Link>
                      </div>
                    )} */}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* 
      <div
        className={clsx(
          "mx-auto w-1300 max-md:w-360",
          "flex",
          "gap-16",
          "mt-60",
          "max-md:mt-60",
          "justify-center",
        )}
      >
        {selectors
          .slice(
            0,
            authUser?.id === user.id ? 2 : 1,
          )
          .map((el) => {
            return (
              <div
                key={el.text}
                className={clsx(
                  "border",
                  "w-150",
                  "rounded-full",
                  selectType === el.type &&
                    "bg-black text-white",
                  "text-center",
                  "py-7",
                  "cursor-pointer",
                )}
                onClick={() => {
                  setSelectType(el.type as any);
                }}
              >
                {el.text}
              </div>
            );
          })}
      </div> */}

      {/* メンバー、ギャラリー、メッセージセクション */}
      <div
        className={clsx(
          "mx-auto w-1300 max-md:w-360",
          "mt-80",
          "flex",
          "flex-col",
          "gap-80",
          "max-md:gap-60",
        )}
      >
        {(isMyPage ||
          (!isMyPage &&
            userPostsCursor.data.length > 0)) && (
          <div className={clsx("mt-0")}>
            {selectType === "user" && (
              <UserPosts />
            )}
            {/* {selectType === "like" && <LikePosts />} */}
          </div>
        )}
        {(isMyPage ||
          (!isMyPage &&
            userStoryPostsCursor.data.length >
              0)) && <UserStoryPosts />}
        {(isMyPage ||
          (!isMyPage &&
            companyMessage?.title &&
            companyMessage?.content)) && (
          <CompanyMessage
            message={companyMessage}
            isEditable={isMyPage}
            userId={user.id}
          />
        )}
        {(isMyPage ||
          (!isMyPage &&
            companyMembers.length > 0)) && (
          <CompanyMembersList
            members={companyMembers}
            basePath={root?.basePath}
            isEditable={isMyPage}
            userId={user.id}
          />
        )}
        {(isMyPage ||
          (!isMyPage &&
            companyGalleries.length > 0)) && (
          <CompanyGalleriesList
            galleries={companyGalleries}
            basePath={root?.basePath}
            isEditable={isMyPage}
            userId={user.id}
          />
        )}

        {(isMyPage ||
          (!isMyPage &&
            Object.values(userProfile).filter(
              (el) => !!el,
            ).length > 3)) && (
          <CompanyInfo
            userProfile={userProfile}
            isEditable={isMyPage}
            userId={user.id}
          />
        )}
      </div>

      {followModalOpen && (
        <FollowUsers
          isFollow={true}
          onClose={() => {
            setFollowModalOpen(false);
          }}
        ></FollowUsers>
      )}
      {followerModalOpen && (
        <FollowUsers
          isFollow={false}
          onClose={() => {
            setFollowerModalOpen(false);
          }}
        ></FollowUsers>
      )}
    </div>
  );
}
